import { NgClass, TitleCasePipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ConfigService } from '../../services/config.service';
import { LocalStorageService } from '../../services/storage.service';
import { BusinessContextService } from '../../services/business-context.service';
import { BusinessSelectDialogComponent, BusinessInfoItem } from '../../auth/business-select-dialog.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [NgClass, RouterLink, TitleCasePipe],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})

export class HeaderComponent {
  @Input() dashboardTitle:any;

  fullScreenClass: boolean = false;
  toggleMode: any;
  elementValue: any;
  localData: any = '';
  currentTitle:string = '';
  userName: string = '';
  businessName: string = '';
  
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private keycloak: KeycloakService,
    private dialog: MatDialog,
    private http: HttpClient,
    private cfg: ConfigService,
    private bizCtx: BusinessContextService,
    private ls: LocalStorageService,
  ) {
    this.route.queryParams.subscribe((params: any) => {
      if (params.theme == 'dark' || params.theme == 'light') {
        localStorage.setItem("data-theme-version", params.theme);
      }
    });
  }
  
  async ngOnInit() {
    // Initial load
    await this.loadUserName();
    await this.loadBusinessName();
    await this.ensureBusinessNameHydratedOnce();

    // React to Keycloak auth events (login, token refresh)
    try {
      (this.keycloak as any)?.keycloakEvents$?.subscribe(async (event: any) => {
        const type = event?.type as KeycloakEventType;
        if (
          type === KeycloakEventType.AuthSuccess ||
          type === KeycloakEventType.AuthRefreshSuccess ||
          type === KeycloakEventType.TokenExpired
        ) {
          await this.loadUserName();
          await this.loadBusinessName();
        }
      });
    } catch { /* no-op */ }
  }

  private async loadUserName() {
    try {
      const logged = await this.keycloak.isLoggedIn();
      const parsed: any = (this.keycloak as any)?.getKeycloakInstance?.()?.tokenParsed || {};
      if (!logged && !parsed) { this.userName = ''; console.log('[Header] Not logged in and no tokenParsed'); return; }

      // Fallback: token parsed data
      console.log('[Header] Using tokenParsed for username', parsed);
      const fullToken = [parsed?.given_name, parsed?.family_name].filter(Boolean).join(' ').trim();
      this.userName = fullToken || parsed?.name || parsed?.preferred_username || parsed?.email || '';
      console.log('[Header] Final userName =', this.userName);
    } catch { this.userName = ''; }
  }


  private async loadBusinessName() {
    try {
      // Usa solo localStorage para evitar pedir /auth/me en cada carga
      const name = this.bizCtx.getSelectedName();
      const uuid = this.bizCtx.getSelected();
      this.businessName = name || uuid || '';
    } catch {
      const selected = this.bizCtx.getSelected();
      this.businessName = selected || '';
    }
  }

  // Mantiene compatibilidad con llamadas existentes sin disparar /auth/me.
  // Si hay nombre almacenado en contexto, lo usa; si no, muestra el UUID.
  private async ensureBusinessNameHydratedOnce() {
    try {
      const name = this.bizCtx.getSelectedName();
      const uuid = this.bizCtx.getSelected();
      if (name && name.length > 0) {
        this.businessName = name;
      } else if (uuid && uuid.length > 0) {
        this.businessName = uuid;
      }
    } catch { /* no-op */ }
  }

  ngAfterContentChecked(){
    if(this.router.url == '/admin'){
      this.currentTitle = 'DashBoard';
    }else { 
      this.currentTitle = this.dashboardTitle?this.dashboardTitle:'DashBoard';
    }
  }

  ngDoCheck() {
    this.themeMode2();
  }

  themeMode2() {    // Theme mode dark - light 
    this.localData = localStorage.getItem('data-theme-version');
    if (this.localData != null) {
      document.body.setAttribute('data-theme-version', this.localData);
    }
  }

  themeMode() {     // Theme mode dark - light 
    this.elementValue = document.body.getAttribute('data-theme-version');
    this.localData = localStorage.getItem('data-theme-version');

    if (this.elementValue == 'light' && this.localData == 'light') {
      this.toggleMode = 'dark';
    } else {
      this.toggleMode = 'light';
    }
    localStorage.setItem("data-theme-version", this.toggleMode);
    this.localData = localStorage.getItem('data-theme-version');
    document.body.setAttribute('data-theme-version', this.localData);
  }

  chatboxActive() { // Chatbox manage
    document.getElementById("chatBox")?.setAttribute("class", "chatbox active");
  }

  chatboxClose() {
    document.getElementById("chatBox")?.setAttribute("class", 'chatbox');
  }

  urlActive(url:any){
    try {
      if (url === '/admin') {
        this.currentTitle = 'Dashboard';
        return;
      }
      const parts = typeof url === 'string' ? url.split('/admin/') : [];
      const afterAdmin = parts.length > 1 ? parts[1] : '';
      const beforeQuery = afterAdmin ? afterAdmin.split('?')[0] : '';
      this.currentTitle = (beforeQuery || 'Dashboard').replace('-', ' ');
    } catch {
      this.currentTitle = 'Dashboard';
    }
  }

  async logout() {
    try {
      // Limpia todo el localStorage y el contexto de negocio antes de salir
      try {
        this.ls.clear();
        this.bizCtx.setSelected(null);
      } catch {}
      await this.keycloak.logout(window.location.origin);
    } catch (e) {
      console.error('Logout error', e);
    }
  }

  // Abre el selector y confirma negocio activo en backend
  async changeBusiness() {
    try {
      const apiUrl = this.cfg.apiUrl;
      if (!apiUrl) { console.warn('[Header] apiUrl vacío'); return; }

      const me: any = await this.http.get(`${apiUrl}/auth/me?v=${Date.now()}`).toPromise();
      const businesses = Array.isArray(me?.businesses) ? me.businesses : [];
      const legacy: BusinessInfoItem[] = Array.isArray(me?.businessUuids)
        ? me.businessUuids.map((uuid: string): BusinessInfoItem => ({ uuid, name: uuid }))
        : [];
      const rawList = (businesses.length > 0 ? businesses : legacy);
      const items: BusinessInfoItem[] = rawList
        .map((b: any): BusinessInfoItem => ({ uuid: (b?.uuid || '').toString(), name: (b?.name || '').toString() }))
        .filter((x: BusinessInfoItem) => !!x.uuid);

      const ref = this.dialog.open(BusinessSelectDialogComponent, { width: '460px', data: { items } });
      const chosen: string | null = await new Promise((resolve) => ref.afterClosed().subscribe((val: string | null) => resolve(val || null)));
      if (!chosen) return;

      const chosenName = items.find((i: BusinessInfoItem) => i.uuid === chosen)?.name || null;
      this.bizCtx.setSelectedWithName(chosen, chosenName);
      this.businessName = chosenName || chosen;

      // Refresca la ruta actual para recargar datos bajo el nuevo negocio
      const current = this.router.url;
      await this.router.navigateByUrl('/', { skipLocationChange: true });
      await this.router.navigateByUrl(current);
    } catch (e) {
      console.error('[Header] Error cambiando negocio', e);
    }
  }

  // Confirmación explícita al backend ya no es necesaria aquí; el interceptor
  // adjuntará X-Business-Uuid solo si está permitido en el token.

}
